<template>
  <hb-basic-page :title="customer.name ? customer.name : ''"
                 :loading="loading"
                 :subtle-loading="subtleLoading"
                 :fill-height="loading"
                 :no-gutters="true"
                 :back-button="{show: true, fallback: 'customers'}"
                 name="single-customer-view">
    <v-col cols="12"  v-if="!loading && customer">
      <v-row justify="center">
        <v-col cols="12" md="10" lg="8" xl="6">
          <v-row justify="center">
            <v-col cols="12">
              <v-card>
                <v-card-title class="headline text-break">
                  {{ $t('customers.basic_details') }}
                </v-card-title>
                <v-card-text>
                  <v-list>
                    <v-list-item class="px-0">
                      <v-list-item-content>
                        <v-list-item-title class="body-1 font-weight-bold text-wrap">{{ $t('customers.business_id') }}:</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-content>
                        <v-list-item-title class="body-1 font-weight-regular text-right text-wrap">{{ customer.businessId }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider />
                    <v-list-item class="px-0">
                      <v-list-item-content>
                        <v-list-item-title class="body-1 font-weight-bold text-wrap">{{ $t('customers.industry') }}:</v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-content>
                        <v-list-item-title class="body-1 font-weight-regular text-right text-wrap">{{ industryName }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions>
                  <v-btn @click="editCustomer" color="secondary" text>{{ $t('edit') }}</v-btn>
                </v-card-actions>
              </v-card>
            </v-col>

            <v-col cols="12">
              <v-card class="rounded my-4">
                <v-card-title class="headline text-break">
                  {{ $tc('job_ads.title', 2) }}
                </v-card-title>
                <v-card-text class="text-center">
                  <v-btn-toggle v-model="selectedStates" active-class="moro">
                    <v-btn :class="JSON.stringify(selectedStates) === '[0,1]' ? 'secondary' : ''" :x-small="$vuetify.breakpoint.xsOnly" :small="$vuetify.breakpoint.smAndUp" :value="[0,1]">{{ $t('job_ads.states.0') }}/{{ $t('job_ads.states.1') }}</v-btn>
                    <v-btn :class="JSON.stringify(selectedStates) === '[2]' ? 'secondary' : ''" :x-small="$vuetify.breakpoint.xsOnly" :small="$vuetify.breakpoint.smAndUp" :value="[2]">{{ $t('job_ads.states.2') }}</v-btn>
                    <v-btn :class="JSON.stringify(selectedStates) === '[4]' ? 'secondary' : ''" :x-small="$vuetify.breakpoint.xsOnly" :small="$vuetify.breakpoint.smAndUp" :value="[4]">{{ $t('job_ads.states.4') }}</v-btn>
                  </v-btn-toggle>
                </v-card-text>
                <v-list v-if="jobAds && jobAds.length > 0">
                  <template v-for="item in jobAds">
                    <JobListItem :job="item" :key="item.id" @copy="copyJobAd" />
                    <v-divider :key="`${item.id}_divider`"></v-divider>
                  </template>
                </v-list>
                <template v-else>
                  <v-card-text class="text-center">
                    {{ $t('job_ads.no_job_ads') }}
                  </v-card-text>
                </template>
                <v-card-text class="text-center">
                  <v-pagination v-model="options.page" :length="length" total-visible="11" @input="getJobAds"/>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>

    <copy-job-ad-dialog ref="copyJobAdDialog" />

    <add-or-edit-customer-dialog
      ref="addOrEditCustomerDialog"
      :industries="allIndustries"
      @save="saveCustomer"
    />
  </hb-basic-page>
</template>

<script>
import AddOrEditCustomerDialog from "@/components/Tenant/Customers/AddOrEditCustomerDialog.vue";
import tenantApi from "@/api/tenant";
import JobListItem from "@/components/Tenant/JobAds/JobListItem.vue";
import CopyJobAdDialog from "@/components/CopyJobAdDialog.vue";

export default {
  name: "SingleCustomerView",
  components: {CopyJobAdDialog, JobListItem, AddOrEditCustomerDialog},
  props: ['id'],
  data() {
    return {
      loading: true,
      subtleLoading: false,
      customer: {},
      allIndustries: [],
      jobAds: [],
      options: {
        page: 0,
        itemsPerPage: 15,
      },
      selectedStates: [1],
    }
  },
  methods: {
    copyJobAd(jobAd) {
      this.$refs.copyJobAdDialog.open(jobAd);
    },
    async reloadCustomer() {
      try {
        const customer = await this.$tenantApi.getCustomer(this.id);
        this.customer = customer;
      } catch (e) {
        this.$handleApiError(e);
      }
    },
    async reloadIndustries() {
      try {
        this.allIndustries = await this.$tenantApi.getIndustries();
      } catch (e) {
        this.$handleApiError(e);
      }
    },
    editCustomer() {
      this.$refs.addOrEditCustomerDialog.open(this.customer);
    },
    async saveCustomer(customer) {
      this.subtleLoading = true;
      try {
        if (customer.id) {
          await this.$tenantApi.updateCustomer(customer.id, customer);
        } else {
          await this.$tenantApi.createCustomer(customer);
        }
        await this.reloadCustomer();
      } catch (error) {
        this.$handleApiError(error);
      }
      this.subtleLoading = false;
    },
    async getJobAds() {
      if (!this.loading) {
        this.subtleLoading = true;
      }
      try {
        const filter = {
          states: this.selectedStates,
          searchString: "",
          customerIds: [this.id],
        };
        const response = await tenantApi.getJobAdsPaged(filter, this.options);
        this.jobAds = [...response.content];
        this.options.page = response.pageable.pageNumber + 1;
        this.length = response.totalPages;
      } catch (error) {
        this.$handleApiError(error);
      }
      this.subtleLoading = false;
    },
  },
  watch: {
    selectedStates() {
      this.$nextTick(() => {
        this.getJobAds();
      })
    }
  },
  computed: {
    industryName() {
      const industry = this.allIndustries.find(i => i.code === this.customer.industryCode);
      return industry ? industry.name : '';
    },
  },
  async mounted() {
    await this.reloadIndustries();
    await this.reloadCustomer();
    await this.getJobAds();
    this.loading = false;
  }
}
</script>

<style scoped>

</style>
